<script setup lang="ts">
  import { computed, ref, useSlots } from 'vue'
  import {
    X,
    ChevronRight,
    ChevronDown,
    Loader2,
    AlertCircle,
    RotateCcw,
    ThumbsUp,
    ThumbsDown,
    CircleCheckBig
  } from 'lucide-vue-next'

  type WidgetStep =
    | 'summary'
    | 'loading'
    | 'review'
    | 'success'
    | 'error'
    | 'feedback'
    | 'feedback-success'

  interface Props {
    open?: boolean
    step?: WidgetStep
    title?: string
    description?: string
    edited?: boolean
    error?: boolean | string
    /** Success step — separate title/description since content differs from summary/review */
    successTitle?: string
    successDescription?: string
    /** Labels — parent provides translated strings */
    reviewLabel?: string
    sendLabel?: string
    discardLabel?: string
    closeLabel?: string
    retryLabel?: string
    errorMessage?: string
    editedLabel?: string
    /** Feedback step labels */
    feedbackTitle?: string
    feedbackDescription?: string
    feedbackReasoningLinkLabel?: string
    feedbackSelectPlaceholder?: string
    feedbackTextareaPlaceholder?: string
    feedbackSubmitLabel?: string
    feedbackCancelLabel?: string
    /** Feedback select options — array of { value, label } */
    feedbackReasonOptions?: Array<{ value: string; label: string }>
    /** Whether the reasoning link should be shown (requires reasoning data to be available) */
    showReasoningLink?: boolean
    /** Whether a feedback submission is in flight */
    feedbackSubmitting?: boolean
    /** Feedback success step */
    feedbackSuccessTitle?: string
    feedbackSuccessDescription?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    open: false,
    step: 'summary',
    title: '',
    description: '',
    edited: false,
    error: false,
    successTitle: '',
    successDescription: '',
    reviewLabel: 'Review email',
    sendLabel: 'Send email',
    discardLabel: 'Discard',
    closeLabel: 'Close',
    retryLabel: 'Retry',
    errorMessage: 'Something went wrong. Please try again.',
    editedLabel: 'Edited',
    feedbackTitle: 'Warum war die Antwort der KI nicht hilfreich?',
    feedbackDescription:
      'Wählen Sie einen Grund aus oder beschreiben Sie kurz, was nicht gepasst hat.',
    feedbackReasoningLinkLabel: 'KI-Überlegungen anzeigen',
    feedbackSelectPlaceholder: 'Grund auswählen',
    feedbackTextareaPlaceholder: 'Beschreibung',
    feedbackSubmitLabel: 'Feedback senden',
    feedbackCancelLabel: 'Abbrechen',
    feedbackReasonOptions: () => [],
    showReasoningLink: false,
    feedbackSubmitting: false,
    feedbackSuccessTitle: 'Feedback sent',
    feedbackSuccessDescription: 'Thank you very much! Your feedback helps us to improve the AI.'
  })

  const emit = defineEmits<{
    (e: 'close'): void
    (e: 'review-click'): void
    (e: 'send-click'): void
    (e: 'discard-click'): void
    (e: 'thumbs-up'): void
    (e: 'thumbs-down'): void
    (e: 'retry'): void
    (e: 'show-reasoning'): void
    (e: 'feedback-submit', payload: { reason: string; comment: string }): void
    (e: 'feedback-cancel'): void
  }>()

  const slots = useSlots()
  const hasSlotContent = computed(() => !!slots.default)

  const effectiveStep = computed<WidgetStep>(() => {
    return props.step
  })

  const activeThumb = ref<'up' | 'down' | null>(null)

  const handleThumbsUp = () => {
    activeThumb.value = 'up'
    emit('thumbs-up')
  }

  const handleThumbsDown = () => {
    activeThumb.value = 'down'
    emit('thumbs-down')
  }

  const feedbackReason = ref('')
  const feedbackComment = ref('')
  const feedbackSelectOpen = ref(false)

  const selectedReasonLabel = computed(() => {
    if (!feedbackReason.value) return ''
    const opt = props.feedbackReasonOptions?.find((o) => o.value === feedbackReason.value)
    return opt?.label ?? ''
  })

  const selectReason = (value: string) => {
    feedbackReason.value = value
    feedbackSelectOpen.value = false
  }

  const canSubmitFeedback = computed(() => !!feedbackReason.value && !props.feedbackSubmitting)

  const handleFeedbackSubmit = () => {
    if (!canSubmitFeedback.value) return
    const payload = {
      reason: feedbackReason.value,
      comment: feedbackComment.value
    }
    feedbackReason.value = ''
    feedbackComment.value = ''
    feedbackSelectOpen.value = false
    emit('feedback-submit', payload)
  }

  const handleFeedbackCancel = () => {
    feedbackReason.value = ''
    feedbackComment.value = ''
    feedbackSelectOpen.value = false
    emit('feedback-cancel')
  }
</script>

<template>
  <Transition name="widget">
    <div
      v-if="open"
      class="ai-widget"
    >
      <!-- Navbar -->
      <div class="ai-widget__navbar">
        <button
          class="ai-widget__close"
          aria-label="Close"
          @click="emit('close')"
        >
          <X :size="20" />
        </button>
      </div>

      <!-- Step content — cross-fade between steps -->
      <Transition
        name="fade"
        mode="out-in"
      >
        <!-- Loading -->
        <div
          v-if="effectiveStep === 'loading'"
          key="loading"
          class="ai-widget__loading"
        >
          <Loader2
            :size="24"
            class="ai-widget__spinner"
          />
        </div>

        <!-- Error -->
        <div
          v-else-if="effectiveStep === 'error'"
          key="error"
          class="ai-widget__body"
        >
          <div class="ai-widget__content ai-widget__content--centered">
            <AlertCircle
              :size="32"
              class="ai-widget__error-icon"
            />
            <p class="ai-widget__error-message">
              {{ typeof error === 'string' ? error : errorMessage }}
            </p>
            <button
              class="ai-widget__btn ai-widget__btn--primary ai-widget__btn--error-retry"
              @click="emit('retry')"
            >
              <RotateCcw :size="16" />
              {{ retryLabel }}
            </button>
          </div>
          <div class="ai-widget__cta">
            <button
              class="ai-widget__btn ai-widget__btn--secondary"
              @click="emit('close')"
            >
              {{ closeLabel }}
            </button>
          </div>
        </div>

        <!-- Summary — initial action notification -->
        <div
          v-else-if="effectiveStep === 'summary'"
          key="summary"
          class="ai-widget__body"
        >
          <div class="ai-widget__content">
            <div class="ai-widget__text">
              <!-- Sparkle icon with gradient stroke -->
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M26.6667 4.00004V9.33337M29.3334 6.66671H24.0001M5.33341 22.6667V25.3334M6.66675 24H4.00008M13.2493 20.6667C13.1303 20.2053 12.8898 19.7842 12.5528 19.4472C12.2159 19.1103 11.7948 18.8698 11.3333 18.7507L3.15333 16.6414C3.01378 16.6018 2.89095 16.5177 2.80348 16.402C2.71602 16.2862 2.6687 16.1451 2.6687 16C2.6687 15.855 2.71602 15.7139 2.80348 15.5981C2.89095 15.4824 3.01378 15.3983 3.15333 15.3587L11.3333 13.248C11.7946 13.1291 12.2156 12.8888 12.5525 12.5521C12.8895 12.2154 13.1301 11.7946 13.2493 11.3334L15.3587 3.15338C15.3979 3.01327 15.4818 2.88984 15.5978 2.80191C15.7137 2.71398 15.8552 2.66638 16.0007 2.66638C16.1462 2.66638 16.2877 2.71398 16.4036 2.80191C16.5195 2.88984 16.6035 3.01327 16.6427 3.15338L18.7507 11.3334C18.8697 11.7948 19.1102 12.2159 19.4472 12.5529C19.7841 12.8898 20.2052 13.1303 20.6667 13.2494L28.8467 15.3574C28.9873 15.3962 29.1114 15.4801 29.1998 15.5961C29.2882 15.7122 29.3361 15.8541 29.3361 16C29.3361 16.146 29.2882 16.2879 29.1998 16.4039C29.1114 16.52 28.9873 16.6039 28.8467 16.6427L20.6667 18.7507C20.2052 18.8698 19.7841 19.1103 19.4472 19.4472C19.1102 19.7842 18.8697 20.2053 18.7507 20.6667L16.6413 28.8467C16.6021 28.9868 16.5182 29.1103 16.4022 29.1982C16.2863 29.2861 16.1448 29.3337 15.9993 29.3337C15.8538 29.3337 15.7123 29.2861 15.5964 29.1982C15.4805 29.1103 15.3965 28.9868 15.3573 28.8467L13.2493 20.6667Z"
                  stroke="url(#sparkle-gradient-summary)"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <defs>
                  <linearGradient
                    id="sparkle-gradient-summary"
                    x1="2.6687"
                    y1="16"
                    x2="29.3361"
                    y2="16"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#EF469B" />
                    <stop
                      offset="1"
                      stop-color="#9333EA"
                    />
                  </linearGradient>
                </defs>
              </svg>
              <h3 class="ai-widget__title ai-widget__title--gradient">
                {{ title }}
              </h3>
              <p class="ai-widget__description">
                {{ description }}
              </p>
            </div>

            <!-- Slot for inline content (e.g. classification form) -->
            <div
              v-if="hasSlotContent"
              class="ai-widget__slot"
            >
              <slot />
            </div>
          </div>

          <!-- CTA — hidden when slot provides its own actions -->
          <div
            v-if="!hasSlotContent"
            class="ai-widget__cta"
          >
            <button
              class="ai-widget__btn ai-widget__btn--primary"
              @click="emit('review-click')"
            >
              {{ reviewLabel }}
              <ChevronRight :size="18" />
            </button>
            <button
              class="ai-widget__btn ai-widget__btn--secondary"
              @click="emit('discard-click')"
            >
              {{ discardLabel }}
            </button>
          </div>
        </div>

        <!-- Review — draft opened for editing -->
        <div
          v-else-if="effectiveStep === 'review'"
          key="review"
          class="ai-widget__body"
        >
          <div class="ai-widget__content">
            <div class="ai-widget__text">
              <!-- Sparkle icon — muted for review step -->
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M26.6667 4.00004V9.33337M29.3334 6.66671H24.0001M5.33341 22.6667V25.3334M6.66675 24H4.00008M13.2493 20.6667C13.1303 20.2053 12.8898 19.7842 12.5528 19.4472C12.2159 19.1103 11.7948 18.8698 11.3333 18.7507L3.15333 16.6414C3.01378 16.6018 2.89095 16.5177 2.80348 16.402C2.71602 16.2862 2.6687 16.1451 2.6687 16C2.6687 15.855 2.71602 15.7139 2.80348 15.5981C2.89095 15.4824 3.01378 15.3983 3.15333 15.3587L11.3333 13.248C11.7946 13.1291 12.2156 12.8888 12.5525 12.5521C12.8895 12.2154 13.1301 11.7946 13.2493 11.3334L15.3587 3.15338C15.3979 3.01327 15.4818 2.88984 15.5978 2.80191C15.7137 2.71398 15.8552 2.66638 16.0007 2.66638C16.1462 2.66638 16.2877 2.71398 16.4036 2.80191C16.5195 2.88984 16.6035 3.01327 16.6427 3.15338L18.7507 11.3334C18.8697 11.7948 19.1102 12.2159 19.4472 12.5529C19.7841 12.8898 20.2052 13.1303 20.6667 13.2494L28.8467 15.3574C28.9873 15.3962 29.1114 15.4801 29.1998 15.5961C29.2882 15.7122 29.3361 15.8541 29.3361 16C29.3361 16.146 29.2882 16.2879 29.1998 16.4039C29.1114 16.52 28.9873 16.6039 28.8467 16.6427L20.6667 18.7507C20.2052 18.8698 19.7841 19.1103 19.4472 19.4472C19.1102 19.7842 18.8697 20.2053 18.7507 20.6667L16.6413 28.8467C16.6021 28.9868 16.5182 29.1103 16.4022 29.1982C16.2863 29.2861 16.1448 29.3337 15.9993 29.3337C15.8538 29.3337 15.7123 29.2861 15.5964 29.1982C15.4805 29.1103 15.3965 28.9868 15.3573 28.8467L13.2493 20.6667Z"
                  stroke="url(#sparkle-gradient-review)"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <defs>
                  <linearGradient
                    id="sparkle-gradient-review"
                    x1="2.6687"
                    y1="16"
                    x2="29.3361"
                    y2="16"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#EF469B" />
                    <stop
                      offset="1"
                      stop-color="#9333EA"
                    />
                  </linearGradient>
                </defs>
              </svg>
              <h3 class="ai-widget__title">
                {{ title }}
              </h3>
              <p class="ai-widget__description">
                {{ description }}
              </p>
              <span
                v-if="edited"
                class="ai-widget__badge"
              >
                {{ editedLabel }}
              </span>
            </div>
          </div>

          <div class="ai-widget__cta">
            <button
              class="ai-widget__btn ai-widget__btn--primary"
              @click="emit('send-click')"
            >
              {{ sendLabel }}
              <ChevronRight :size="18" />
            </button>
            <button
              class="ai-widget__btn ai-widget__btn--secondary"
              @click="emit('discard-click')"
            >
              {{ discardLabel }}
            </button>
          </div>
        </div>

        <!-- Success — action completed -->
        <div
          v-else-if="effectiveStep === 'success'"
          key="success"
          class="ai-widget__body"
        >
          <div class="ai-widget__content">
            <!-- Success content: icon + title + description + rating, gap 24px per Figma -->
            <div class="ai-widget__text ai-widget__text--success">
              <!-- lucide-circle-check-big per Figma -->
              <CircleCheckBig
                :size="32"
                stroke-width="1.5"
                class="ai-widget__success-icon"
              />
              <div class="ai-widget__text-body">
                <h3 class="ai-widget__title">
                  {{ successTitle || title }}
                </h3>
                <p class="ai-widget__description">
                  {{ successDescription || description }}
                </p>
              </div>
              <!-- AI message toolbar — thumbs up/down -->
              <div class="ai-widget__rating">
                <button
                  class="ai-widget__rating-btn"
                  :class="{ 'ai-widget__rating-btn--active': activeThumb === 'up' }"
                  aria-label="Thumbs up"
                  @click="handleThumbsUp"
                >
                  <ThumbsUp :size="20" />
                </button>
                <button
                  class="ai-widget__rating-btn"
                  :class="{ 'ai-widget__rating-btn--active': activeThumb === 'down' }"
                  aria-label="Thumbs down"
                  @click="handleThumbsDown"
                >
                  <ThumbsDown :size="20" />
                </button>
              </div>
            </div>
          </div>

          <div class="ai-widget__cta">
            <button
              class="ai-widget__btn ai-widget__btn--secondary"
              @click="emit('close')"
            >
              {{ closeLabel }}
            </button>
          </div>
        </div>

        <!-- Feedback — negative rating form -->
        <div
          v-else-if="effectiveStep === 'feedback'"
          key="feedback"
          class="ai-widget__feedback"
        >
          <!-- Text block: title + description + reasoning link -->
          <div class="ai-widget__feedback-text">
            <h3 class="ai-widget__title">
              {{ feedbackTitle }}
            </h3>
            <p class="ai-widget__description">
              {{ feedbackDescription }}
            </p>
            <button
              v-if="showReasoningLink"
              class="ai-widget__reasoning-link"
              @click="emit('show-reasoning')"
            >
              {{ feedbackReasoningLinkLabel }}
            </button>
          </div>

          <!-- Form inputs -->
          <div class="ai-widget__feedback-inputs">
            <!-- Custom select dropdown -->
            <div class="ai-widget__feedback-select-wrapper">
              <button
                class="ai-widget__feedback-select"
                @click="feedbackSelectOpen = !feedbackSelectOpen"
              >
                <span :class="{ 'ai-widget__feedback-select-placeholder': !feedbackReason }">
                  {{ selectedReasonLabel || feedbackSelectPlaceholder }}
                </span>
                <ChevronDown :size="20" />
              </button>
              <div
                v-if="feedbackSelectOpen"
                class="ai-widget__feedback-dropdown"
              >
                <button
                  v-for="option in feedbackReasonOptions"
                  :key="option.value"
                  class="ai-widget__feedback-dropdown-item"
                  :class="{
                    'ai-widget__feedback-dropdown-item--selected': feedbackReason === option.value
                  }"
                  @click="selectReason(option.value)"
                >
                  {{ option.label }}
                </button>
              </div>
            </div>

            <!-- Textarea -->
            <textarea
              v-model="feedbackComment"
              class="ai-widget__feedback-textarea"
              :placeholder="feedbackTextareaPlaceholder"
            ></textarea>
          </div>

          <!-- CTA buttons -->
          <div class="ai-widget__cta">
            <button
              class="ai-widget__btn ai-widget__btn--primary"
              :disabled="!canSubmitFeedback"
              @click="handleFeedbackSubmit"
            >
              {{ feedbackSubmitLabel }}
              <ChevronRight :size="18" />
            </button>
            <button
              class="ai-widget__btn ai-widget__btn--secondary"
              @click="handleFeedbackCancel"
            >
              {{ feedbackCancelLabel }}
            </button>
          </div>
        </div>

        <!-- Feedback Success — confirmation after submitting feedback -->
        <div
          v-else-if="effectiveStep === 'feedback-success'"
          key="feedback-success"
          class="ai-widget__body"
        >
          <div class="ai-widget__content">
            <div class="ai-widget__text">
              <CircleCheckBig
                :size="32"
                stroke-width="1.5"
                class="ai-widget__success-icon"
              />
              <h3 class="ai-widget__title">
                {{ feedbackSuccessTitle }}
              </h3>
              <p class="ai-widget__description">
                {{ feedbackSuccessDescription }}
              </p>
            </div>
          </div>

          <div class="ai-widget__cta">
            <button
              class="ai-widget__btn ai-widget__btn--secondary"
              @click="emit('close')"
            >
              {{ closeLabel }}
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </Transition>
</template>

<style scoped>
  .ai-widget {
    position: fixed;
    /* 24px (launcher bottom) + 64px (launcher height) + 12px (gap) */
    bottom: 100px;
    right: 24px;
    z-index: 999;
    display: flex;
    flex-direction: column;
    width: min(400px, calc(100vw - 48px));
    height: min(600px, calc(100vh - 124px));
    background: #ffffff;
    border-radius: 16px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  /* Navbar */
  .ai-widget__navbar {
    display: flex;
    justify-content: flex-end;
    padding: 16px 16px 0 16px;
  }

  .ai-widget__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 6px;
    transition:
      color 0.15s ease,
      background-color 0.15s ease;
  }

  .ai-widget__close:hover {
    color: #4b5563;
    background-color: #f3f4f6;
  }

  /* Loading state — spinner top-left per Figma */
  .ai-widget__loading {
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 16px;
  }

  .ai-widget__spinner {
    color: #c084fc;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  /* Error state — uses standard body/content/cta layout */
  .ai-widget__content--centered {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    text-align: center;
  }

  .ai-widget__btn--error-retry {
    width: auto;
    padding: 0 24px;
    margin-top: 4px;
  }

  .ai-widget__error-icon {
    color: #ef4444;
  }

  .ai-widget__error-message {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }

  /* Body — flex wrapper for content + CTA; gap matches Figma Fill item-spacing: 16 */
  .ai-widget__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 0;
  }

  /* Content area — fills available space */
  .ai-widget__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0 16px;
    overflow-y: auto;
  }

  /* Text block */
  .ai-widget__text {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* Slot for inline flow content (e.g. classification form) */
  .ai-widget__slot {
    margin-top: 8px;
  }

  .ai-widget__title {
    font-family: 'Inter', sans-serif;
    font-size: 18px;
    font-weight: 500;
    line-height: 28px;
    letter-spacing: 0;
    max-width: 324px;
    margin: 0;
    color: #1f2937;
  }

  .ai-widget__title--gradient {
    white-space: pre-line;
    background: linear-gradient(135deg, rgba(239, 70, 154, 1), rgba(147, 51, 234, 1));
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }

  .ai-widget__title--muted {
    color: #9ca3af;
  }

  .ai-widget__description {
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: #1f2937;
    max-width: 368px;
    margin: 0;
  }

  .ai-widget__description--muted {
    color: #9ca3af;
  }

  /* Bearbeitet badge */
  .ai-widget__badge {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 4px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 400;
    color: #374151;
    margin-top: 4px;
  }

  /* Success step — 24px gap between icon / text-body / rating per Figma */
  .ai-widget__text--success {
    gap: 24px;
  }

  /* Inner title + description group — 8px gap per Figma */
  .ai-widget__text-body {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .ai-widget__success-icon {
    color: #1f2937;
    flex-shrink: 0;
  }

  /* Rating buttons (success step) */
  .ai-widget__rating {
    display: flex;
    gap: 8px;
    margin-top: 8px;
  }

  .ai-widget__rating-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    background: none;
    color: #9ca3af;
    cursor: pointer;
    border-radius: 6px;
    transition:
      color 0.15s ease-in-out,
      background-color 0.15s ease-in-out;
  }

  .ai-widget__rating-btn:hover {
    color: #9333ea;
    background-color: #f5f3ff;
  }

  .ai-widget__rating-btn--active {
    color: #9333ea;
  }

  /* CTA section — pinned to bottom */
  .ai-widget__cta {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 16px 16px 16px;
  }

  .ai-widget__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;
    height: 48px;
    border-radius: 999px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition:
      background-color 0.15s ease,
      opacity 0.15s ease;
  }

  .ai-widget__btn--primary {
    background: #1f2937;
    color: #ffffff;
    border: none;
  }

  /* Pin chevron to the right edge of CTA buttons */
  .ai-widget__btn--primary:not(.ai-widget__btn--error-retry) :deep(svg) {
    position: absolute;
    right: 16px;
  }

  .ai-widget__btn--primary:hover:not(:disabled) {
    background: #111827;
  }

  .ai-widget__btn--primary:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .ai-widget__btn--secondary {
    background: transparent;
    color: #6b7280;
    border: 1px solid #e5e7eb;
  }

  .ai-widget__btn--secondary:hover {
    background: #f9fafb;
    color: #4b5563;
  }

  .ai-widget__btn--inline {
    width: auto;
    height: 40px;
    padding: 0 20px;
  }

  /* Feedback step — matches Figma 04_AIA - Feedback */
  .ai-widget__feedback {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 0 16px 12px 16px;
    min-height: 0;
  }

  .ai-widget__feedback-text {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .ai-widget__reasoning-link {
    display: inline-flex;
    align-items: center;
    width: fit-content;
    padding: 0;
    border: none;
    background: none;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: #9333ea;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .ai-widget__reasoning-link:hover {
    color: #7e22ce;
  }

  .ai-widget__feedback-inputs {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-height: 0;
  }

  .ai-widget__feedback-select-wrapper {
    position: relative;
  }

  .ai-widget__feedback-select {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 40px;
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #1f2937;
    cursor: pointer;
    transition: border-color 0.15s ease;
  }

  .ai-widget__feedback-select:hover {
    border-color: #d1d5db;
  }

  .ai-widget__feedback-select-placeholder {
    color: #9ca3af;
  }

  .ai-widget__feedback-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 10;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.08);
    overflow: hidden;
  }

  .ai-widget__feedback-dropdown-item {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 8px 10px;
    border: none;
    background: none;
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #1f2937;
    cursor: pointer;
    text-align: left;
    transition: background-color 0.1s ease;
  }

  .ai-widget__feedback-dropdown-item:hover {
    background-color: #f9fafb;
  }

  .ai-widget__feedback-dropdown-item--selected {
    background-color: #f5f3ff;
    color: #9333ea;
  }

  .ai-widget__feedback-textarea {
    flex: 1;
    width: 100%;
    min-height: 176px;
    padding: 8px 10px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.05);
    font-family: 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #1f2937;
    resize: none;
    transition: border-color 0.15s ease;
  }

  .ai-widget__feedback-textarea::placeholder {
    color: #9ca3af;
  }

  .ai-widget__feedback-textarea:focus {
    outline: none;
    border-color: #9333ea;
  }

  /* Cross-fade between steps — 200ms */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  /* Outer widget show/hide transitions */
  .widget-enter-active {
    transition:
      opacity 0.2s ease-out,
      transform 0.2s ease-out;
  }

  .widget-leave-active {
    transition:
      opacity 0.15s ease-in,
      transform 0.15s ease-in;
  }

  .widget-enter-from,
  .widget-leave-to {
    opacity: 0;
    transform: translateY(16px);
  }
</style>
